/**
 * @file Mobile menu drawer (snippets/menu-drawer.liquid). Registers <bk-menu-drawer>, a BkDialog
 * (@theme/dialog: modal <dialog>, focus trap, scroll lock, Escape, backdrop click, return focus).
 *
 * - open(trigger): opens, then expands the item named by the trigger's data-menu-target or
 *   data-menu-drawer-open value: "collections" expands the first item whose links are all
 *   collections, else the first one holding a collection link (mobile tab bar "Categories");
 *   any other value is a menu item handle.
 * - Any [data-menu-drawer-open] in the document opens the drawer (plain clicks only, so the
 *   element's own link still works without JS); [data-dialog-open="MenuDrawer"] works through BkDialog.
 * - Closes when a same-page anchor inside it is followed, and when the desktop menu takes over
 *   (990 px and wider) unless the header collapsed to the burger. Escape closes it from any
 *   control inside (explicit keydown handling on top of the native cancel request).
 * - After closing, collapses the disclosures opened during the visit (not the one holding the
 *   current page).
 */
import { BkDialog } from '@theme/dialog';

const DESKTOP_NAV_QUERY = '(min-width: 990px)';

class BkMenuDrawer extends BkDialog {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #collapseTimer;

  #desktop = matchMedia(DESKTOP_NAV_QUERY);

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('click', (event) => this.#onOpenerClick(event), { signal });
    this.addEventListener('click', (event) => this.#onLinkClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.#desktop.addEventListener('change', () => this.#onViewportChange(), { signal });
    document.addEventListener(
      'bk:dialog:close',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        if (detail?.id === this.id) this.#scheduleCollapse();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#collapseTimer);
  }

  /**
   * Open the drawer, optionally at a menu item.
   * @param {HTMLElement | null} [trigger]
   */
  open(trigger) {
    clearTimeout(this.#collapseTimer);
    super.open(trigger);
    const target = trigger?.dataset.menuTarget || trigger?.dataset.menuDrawerOpen || '';
    if (target) this.expand(target);
  }

  /**
   * Expand a top-level menu item and move focus to it.
   * @param {string} key - "collections" or a menu item handle
   */
  expand(key) {
    const details =
      key === 'collections'
        ? (this.querySelector('details[data-menu-collections="all"]') ??
          this.querySelector('details[data-menu-collections]'))
        : this.querySelector(`details[data-menu-key="${CSS.escape(key)}"]`);
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = true;
    const summary = details.querySelector(':scope > summary');
    if (!(summary instanceof HTMLElement)) return;
    summary.focus();
    summary.scrollIntoView({ block: 'nearest' });
  }

  /** @param {MouseEvent} event */
  #onOpenerClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    const opener = target instanceof Element ? target.closest('[data-menu-drawer-open]') : null;
    if (!(opener instanceof HTMLElement) || this.contains(opener)) return;
    if (document.querySelector('bk-menu-drawer') !== this) return;
    event.preventDefault();
    this.open(opener);
  }

  /**
   * Escape closes the drawer from any control inside it. Handled here as well as by the native
   * cancel request, which some browsers and assistive tools do not deliver to a modal dialog.
   * @param {KeyboardEvent} event
   */
  #onKeydown(event) {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing || !this.isOpen) return;
    event.preventDefault();
    this.close();
  }

  /** @param {MouseEvent} event */
  #onLinkClick(event) {
    const target = event.target;
    const link = target instanceof Element ? target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    const url = new URL(link.href, window.location.href);
    const samePage = url.origin === window.location.origin && url.pathname === window.location.pathname;
    if (samePage && url.hash) this.close();
  }

  #onViewportChange() {
    if (!this.#desktop.matches || !this.isOpen) return;
    if (document.querySelector('bk-header[data-nav-overflow]')) return;
    this.close();
  }

  #scheduleCollapse() {
    clearTimeout(this.#collapseTimer);
    this.#collapseTimer = setTimeout(() => {
      if (this.isOpen) return;
      for (const details of this.querySelectorAll('details[open]')) {
        if (!(details instanceof HTMLDetailsElement)) continue;
        if (details.querySelector('[aria-current="page"]')) continue;
        details.open = false;
      }
    }, 400);
  }
}

if (!customElements.get('bk-menu-drawer')) customElements.define('bk-menu-drawer', BkMenuDrawer);
